import { ConfigServiceProvider } from '../config/config.service';
import { DictionaryItem } from '../../interfaces/dictionary-item.interface';
import { FormField } from '../../interfaces/form-field.interface';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { ContentPage } from '../../interfaces/content-page.interface';
import { Utils } from '../../utils/utils.class';
import { ReferenceField } from '../../interfaces/reference-field.interface';
import { Observable } from 'rxjs';
import { PredefinedValue } from '../../interfaces/predefined-value.interface';
import { SortOption } from '../../interfaces/sort-option.interface';

@Injectable()
export class DataSourceOptionsService {
  constructor(@Inject(ConfigServiceProvider) private confService: ConfigServiceProvider, @Inject(HttpClient) private http: HttpClient) {}

  public downloadFieldsOptions(fields: FormField[], backendBaseAPI: string): void {
    if (!backendBaseAPI || !fields) {
      console.error('DataSourceOptionsService: fields or backendBaseAPI is missing');
      return;
    }

    fields.forEach((field: FormField) => this.downloadFieldOptions(field, backendBaseAPI));
  }

  private downloadFieldOptions(field: FormField, backendBaseAPI: string): void {
    if (field.dataSource === 'dataView' || field.type === 'reference') {
      this.getReferenceValue(field, backendBaseAPI).subscribe(
        (data: ReferenceField[]) => {
          field.values = data.map((v) => ({ option: v, visible: true }));
        },
        (error) => console.error(error),
      );
    } else if ((field.dataSource === 'dictionary' || field.type === 'dictionary') && field.dictionaryId) {
      this.getDictionaryItemValues(field, backendBaseAPI).subscribe(
        (data: DictionaryItem[]) => {
          field.values = data.map((v) => ({ option: v, visible: true }));
        },
        (error) => console.error(error),
      );
    } else if (field.dataSource === 'predefined') {
      field.values = (field.values as any as PredefinedValue[]).map((v) => ({ visible: true, option: v }));
    } else if (field.type === 'list') {
      this.downloadFieldsOptions(field.subForm.definition, backendBaseAPI);
    }
  }

  private getDataSourceFiltersFromField(field: FormField): string {
    if(field.dataSourceParams && field.dataSourceParams.httpParams && field.dataSourceParams.httpParams.filters)
      return field.dataSourceParams.httpParams.filters;
    return null;
  }

  private getDataSourceSortFromField(field: FormField): SortOption[] {
    if(field.dataSourceParams && field.dataSourceParams.sort)
      return field.dataSourceParams.sort;
    return null;
  }

  public getReferenceValue(field: FormField, backendBaseAPI: string, searchPhase: string = null): Observable<ReferenceField[]> {
    let filters = this.getDataSourceFiltersFromField(field);
    let sort = this.getDataSourceSortFromField(field) ? this.getDataSourceSortFromField(field) : field.sort;
    return this.http.get<ContentPage>(this.confService.getUrlWithParameter(backendBaseAPI, 'dataView', field.dataView, sort, searchPhase, filters)).pipe(
      take(1),
      map((res) =>
        res.content.map((elem) => Utils.evaluateReferenceDisplayStrings(elem, field)).sort((a, b) => this.sortReferenceFields(a, b, field)),
      ),
    );
  }

  private sortReferenceFields(a: ReferenceField, b: ReferenceField, field: FormField): number {
    if (field.sort && field.sort[0]) {
      return this.genericSortFunction(a.sortByValue, b.sortByValue, field.sort);
    }
    if (field.rowFormat) {
      return this.sortReferenceFieldsByRowFormatTitle(a, b);
    }
    if (field.dataViewColumn) {
      return this.sortReferenceFieldsByDataViewColumn(a, b);
    }
    throw new Error('[DataSourceOptionsService] Missing ReferenceField display configuration!');
  }

  private sortReferenceFieldsByRowFormatTitle(a: ReferenceField, b: ReferenceField): number {
    if (a.rowFormat.title < b.rowFormat.title) {
      return -1;
    }
    if (a.rowFormat.title > b.rowFormat.title) {
      return 1;
    }
    return 0;
  }

  private sortReferenceFieldsByDataViewColumn(a: ReferenceField, b: ReferenceField): number {
    if (a.dataViewColumn < b.dataViewColumn) {
      return -1;
    }
    if (a.dataViewColumn > b.dataViewColumn) {
      return 1;
    }
    return 0;
  }

  public getDictionaryItemValues(field: FormField, backendBaseAPI: string): Observable<DictionaryItem[]> {
    return this.http.get<DictionaryItem[]>(this.confService.getUrlWithParameter(backendBaseAPI, 'definition', field.dictionaryId, field.sort)).pipe(
      take(1),
      map((values) => values.filter((a) => a.deleted !== true).sort((a, b) => this.sortDictionaryItems(a, b, field))),
    );
  }

  private sortDictionaryItems(a: DictionaryItem, b: DictionaryItem, field: FormField): number {
    let sortConfig = [{
      property: 'name',
      direction: 'ASC'
    } as SortOption];
    if(field.sort) {
      sortConfig = field.sort
    }
    return this.genericSortFunction(a, b, sortConfig );
  }

  private genericSortFunction(a: any, b: any, sort: SortOption[]) {
    for (let column of sort) {
      const {property, direction = 'ASC'  } = column;
      const valueA = a[property];
      const valueB = b[property];

      if (valueA < valueB) return direction === 'ASC' ? -1 : 1;
      if (valueA > valueB) return direction === 'ASC' ? 1 : -1;
    }
    return 0;
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_forms/projects/forms/src/lib/services/data-source-options/data-source-options.service.ts